// 동료 관리자 - 동료들의 생성, 관리, 업데이트를 담당
class CompanionManager {
  constructor(scene, player, gameStateManager) {
    this.scene = scene;
    this.player = player;
    this.gameStateManager = gameStateManager;

    // 동료 목록
    this.companions = [];
    this.maxCompanions = 100; // 최대 동료 수

    // 적 목록 참조 (새로 소환되는 동료에게 전달하기 위해 저장)
    this.enemies = [];

    // 동료 타입별 설정
    this.companionTypes = {
      ROGUE: {
        class: RogueCompanion,
        config: CompanionsConfig.ROGUE,
      },
      MAGE: {
        class: MageCompanion,
        config: CompanionsConfig.MAGE,
      },
      BARBARIAN: {
        class: BarbarianCompanion,
        config: CompanionsConfig.BARBARIAN,
      },
    };

    // 동료 소환 위치 오프셋
    this.spawnOffsets = [
      { x: -2, z: 0 }, // 왼쪽
      { x: 2, z: 0 }, // 오른쪽
      { x: 0, z: 2 }, // 앞쪽
      { x: 0, z: -2 }, // 뒤쪽
    ];
  }

  // 동료 소환
  async spawnCompanion(type) {
    if (this.companions.length >= this.maxCompanions) {
      return false;
    }

    const companionInfo = this.companionTypes[type];
    if (!companionInfo) {
      return false;
    }

    try {
      // 동료 생성
      const companion = new companionInfo.class(
        this.scene,
        this.player,
        companionInfo.config
      );

      // 소환 위치 설정
      const spawnIndex = this.companions.length % this.spawnOffsets.length;
      const offset = this.spawnOffsets[spawnIndex] || { x: 0, z: 0 };
      companion.position.x = this.player.position.x + offset.x;
      companion.position.y = this.player.position.y;
      companion.position.z = this.player.position.z + offset.z;

      // 새로 생성된 동료에게 현재 적 목록 전달 (빈 배열이어도 참조 연결이 중요)
      companion.setEnemies(this.enemies);

      // 한국어 주석: 동료의 baseStats 초기화 및 파티 버프 적용
      if (companion.updateBaseStats) {
        companion.updateBaseStats();
      }
      if (companion.applyPartyBuffs) {
        companion.applyPartyBuffs();
      }

      // 동료 목록에 추가
      this.companions.push(companion);

      // 한국어 주석: 모델 로딩이 완료될 때까지 대기
      // Companion 생성자에서 init()을 호출하지만, 이미 호출되었을 수도 있음
      // isLoaded를 체크하여 로딩 완료 확인
      let maxWaitTime = 3000; // 최대 3초 대기
      const startTime = Date.now();
      while (!companion.isLoaded && Date.now() - startTime < maxWaitTime) {
        await new Promise((resolve) => setTimeout(resolve, 50)); // 50ms마다 체크
      }

      // 한국어 주석: 동료 소환 로그 표시 (해당 클래스의 동료 수 포함)
      if (companion.isLoaded && window.game && window.game.uiManager) {
        const companionName = companionInfo.config.NAME || type;
        const classCompanionCount = this.getCompanionCount(type);
        window.game.uiManager.showCompanionLog(
          companionName,
          true,
          classCompanionCount
        );
      }

      return companion.isLoaded; // 모델 로딩 성공 여부 반환
    } catch (error) {
      return false;
    }
  }

  // 동료 제거
  removeCompanion(companion) {
    const index = this.companions.indexOf(companion);
    if (index > -1) {
      // 한국어 주석: 제거 전에 해당 클래스의 동료 수 계산 (사망 로그 표시용)
      const companionType = companion.type;
      const companionInfo = this.companionTypes[companionType];

      // 동료 제거 처리
      companion.dispose();
      this.companions.splice(index, 1);

      // 한국어 주석: 동료 사망 로그 표시 (제거 후의 해당 클래스 동료 수)
      if (window.game && window.game.uiManager && companionInfo) {
        const companionName = companionInfo.config.NAME || companionType;
        const classCompanionCount = this.getCompanionCount(companionType);
        window.game.uiManager.showCompanionLog(
          companionName,
          false,
          classCompanionCount
        );
      }

      return true;
    }
    return false;
  }

  // 모든 동료 제거
  removeAllCompanions() {
    this.companions.forEach((companion) => companion.dispose());
    this.companions = [];

    // 한국어 주석: 적 목록 참조도 초기화 (게임 재시작 시 깔끔하게)
    this.enemies = [];
  }

  // 환경 오브젝트 설정 (모든 동료에게 전달)
  setEnvironmentObjects(objects) {
    this.companions.forEach((companion) => {
      companion.setEnvironmentObjects(objects);
    });
  }

  // 적들 참조 설정 (모든 동료에게 전달)
  setEnemies(enemies) {
    // 적 목록 참조 저장 (나중에 스폰될 동료들을 위해)
    this.enemies = enemies;

    // 기존 동료들에게 적 목록 전달
    this.companions.forEach((companion) => {
      companion.setEnemies(enemies);
    });
  }

  // 동료 업데이트
  update(deltaTime, enemies, camera = null) {
    // 동료들 업데이트 (죽음 애니메이션 재생 중인 동료도 포함)
    for (let i = this.companions.length - 1; i >= 0; i--) {
      const companion = this.companions[i];

      // 한국어 주석: 살아있거나 죽음 애니메이션 재생 중이면 업데이트
      if (companion.isAlive || companion.isDying) {
        // 다른 동료들과의 충돌 회피를 위해 동료 목록 전달, 카메라도 전달
        companion.update(deltaTime, enemies, this.companions, camera);
      } else {
        // 한국어 주석: 죽음 애니메이션까지 끝난 동료만 제거
        this.removeCompanion(companion);
      }
    }
  }

  // 동료 업그레이드
  upgradeCompanion(type, level) {
    const companionInfo = this.companionTypes[type];
    if (!companionInfo) return false;

    // 해당 타입의 동료 찾기
    const companion = this.companions.find((c) => c.type === type);
    if (!companion) return false;

    // 업그레이드 효과 적용
    const upgradeEffect = UpgradeConfig.COMPANIONS[type].EFFECTS[level - 1];
    if (upgradeEffect) {
      this.applyUpgradeEffect(companion, upgradeEffect);
      return true;
    }

    return false;
  }

  // 업그레이드 효과 적용 (멤버 변수 사용)
  applyUpgradeEffect(companion, effect) {
    Object.keys(effect).forEach((key) => {
      switch (key) {
        case "attackRange":
          companion.attackRange *= effect[key];
          companion.attackDistance *= effect[key];
          break;
        case "attackDamage":
          companion.attackDamage *= effect[key];
          break;
        case "attackSpeed":
          companion.attackCooltime /= effect[key];
          // 공격 애니메이션 속도도 조정
          if (companion.attackAction) {
            const duration = companion.attackAction.getClip().duration;
            companion.attackAction.setEffectiveTimeScale(
              duration / companion.attackCooltime
            );
          }
          break;
        case "moveSpeed":
          companion.moveSpeed *= effect[key];
          break;
        case "maxHealth":
          const healthRatio = companion.health / companion.maxHealth;
          companion.maxHealth *= effect[key];
          companion.health = Math.floor(companion.maxHealth * healthRatio);
          break;
      }
    });

    // 한국어 주석: 업그레이드 후 baseStats 업데이트 및 파티 버프 재적용
    if (companion.updateBaseStats) {
      companion.updateBaseStats();
    }
    if (companion.applyPartyBuffs) {
      companion.applyPartyBuffs();
    }
  }

  // 동료 상태 정보 반환
  getCompanionStatus() {
    return this.companions.map((companion) => ({
      type: companion.type,
      health: companion.health,
      maxHealth: companion.maxHealth,
      isAlive: companion.isAlive,
      position: { ...companion.position },
    }));
  }

  // 특정 타입의 동료 수 반환
  getCompanionCount(type) {
    return this.companions.filter((c) => c.type === type).length;
  }

  // 동료 소환 가능 여부 체크
  canSpawnCompanion(type) {
    const companionInfo = this.companionTypes[type];
    if (!companionInfo) return false;

    return this.companions.length < this.maxCompanions;
  }

  // 동료 정보 반환 (UI용)
  getCompanionInfo(type) {
    const companionInfo = this.companionTypes[type];
    if (!companionInfo) return null;

    return {
      name: companionInfo.config.NAME,
      canSpawn: this.canSpawnCompanion(type),
      count: this.getCompanionCount(type),
    };
  }

  // 모든 동료 정보 반환
  getAllCompanionInfo() {
    const info = {};
    Object.keys(this.companionTypes).forEach((type) => {
      info[type] = this.getCompanionInfo(type);
    });
    return info;
  }

  // 정리
  dispose() {
    this.removeAllCompanions();
  }
}

// 전역으로 내보내기
if (typeof module !== "undefined" && module.exports) {
  module.exports = CompanionManager;
} else {
  window.CompanionManager = CompanionManager;
}

// webpack 빌드를 위해 default export 추가
export default CompanionManager;
